import { useEffect, useState } from 'react';
import { useQuest } from './useQuest';
import { quest } from '../utils/quest';
import type { FaceGlyph } from '../utils/faceGlyph';

/**
 * Where Pixel should be during the quest.
 * dock = the chat corner (where he is the whole hunt, and the only place he sends messages);
 * page = pinned to a spot on the page itself (waiting in the middle of the footer) — scroll away
 *        and he stays there;
 * roam = a fixed point on screen (the launch).
 */
export type QuestPilot =
  | { mode: 'off' }
  | { mode: 'dock' }
  | {
      mode: 'page';
      x: number;
      docY: number;
      speed: number;
      glyph: FaceGlyph | null;
      /** A quick happy hop on the spot (with a grin) */
      celebrate?: boolean;
    }
  | {
      mode: 'roam';
      x: number;
      y: number;
      speed: number;
      /** null = his own face */
      glyph: FaceGlyph | null;
      /** Optional vertical keyframes (the lift-off) with their timing (0..1) */
      ys?: number[];
      times?: number[];
      /** Accelerate instead of the usual smooth glide (lift-off) */
      accelerate?: boolean;
    };

/** Lift-off: duration of his climb up and out of the screen (s) — matches the page rush in QuestFinale */
export const LIFTOFF_S = 3.4;
/** Phones, talking in the footer: across the screen (0..1) — the same as his first chat spot — */
const MOBILE_TALK_X = 0.3;
/** …and this share of the screen height below the footer's middle */
const MOBILE_TALK_DROP = 0.1;

/** The middle of the footer, in page coordinates (where he sits outside the quest, too) */
function footerMiddle(): { x: number; docY: number } | null {
  const el = document.querySelector<HTMLElement>('[data-drone-footer]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: (r.left + r.width / 2) / window.innerWidth, docY: r.top + r.height / 2 + window.scrollY };
}

/**
 * Directs Pixel through the quest. He stays in the chat corner for the whole hunt, giving clues.
 * Once all five shards are found and he's said so, he flies to the middle of the footer by himself
 * and waits there (pinned to the page, not your screen) for the timing game, the talk and the
 * take-off charge — and lifts off from there.
 */
export function useQuestPilot(): QuestPilot {
  const state = useQuest();
  const active = state.status === 'active';
  const inFooter = active && state.step === 'finale' && state.finaleStage !== 'rise';

  // The footer moves a little as the page settles (images, its own parallax): keep measuring
  const [spot, setSpot] = useState<{ x: number; docY: number } | null>(null);
  useEffect(() => {
    if (!inFooter) return;
    const measure = () => {
      const next = footerMiddle();
      setSpot((cur) =>
        next && (!cur || Math.abs(cur.docY - next.docY) > 6 || Math.abs(cur.x - next.x) > 0.01) ? next : cur,
      );
    };
    measure();
    const id = window.setInterval(measure, 500);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', measure);
    };
  }, [inFooter]);

  const pilot = decide();
  // The 3D scene reads his face sign straight from the store
  quest.faceGlyph = pilot.mode === 'roam' || pilot.mode === 'page' ? pilot.glyph : null;
  return pilot;

  function decide(): QuestPilot {
    if (!active) return { mode: 'off' };
    if (state.step !== 'finale') return { mode: 'dock' };
    // Phones: while he talks (and celebrates) he sits like in his first chat — over on the left and
    // a bit lower, so his notes open up toward the upper right; for the timing game and the charge
    // he glides back to the middle, where he's easiest to tap
    const talkSpot = window.innerWidth < 768 && state.finaleStage !== 'qte' && state.finaleStage !== 'charge';
    switch (state.finaleStage) {
      case 'talk':
      case 'celebrate':
      case 'qte':
      case 'charge':
        if (!spot) return { mode: 'dock' };
        return {
          mode: 'page',
          x: talkSpot ? MOBILE_TALK_X : spot.x,
          docY: talkSpot ? spot.docY + window.innerHeight * MOBILE_TALK_DROP : spot.docY,
          speed: 1.6,
          // The timing game is drawn on his face; otherwise his own face (charging: his battery screen)
          glyph: state.finaleStage === 'qte' ? quest.qteGlyph : null,
          celebrate: state.finaleStage === 'celebrate',
        };
      case 'rise': {
        // Lifts off from right where he is: a slow first climb, then faster and faster off the top
        const { x, y } = quest.liftFrom;
        return {
          mode: 'roam',
          x,
          y: -0.9,
          ys: [y, y - 0.08, y - 0.3, -0.9],
          times: [0, 0.4, 0.75, 1],
          speed: LIFTOFF_S,
          accelerate: true,
          // His own face: grinning and chattering away all the way up (see DroneCompanion)
          glyph: null,
        };
      }
    }
  }
}
